// Kontakter — samlet, søkbar adressebok. Gjør det ENKELT å finne noen å sende
// 1:1 til: HV-enheter (@skade_…), lagrede GNÅ-kontakter, og GNÅ-brukere man har
// fått melding fra (auto-lært). Søk på tvers, ett-klikks 1:1 som ÅPNER samtalen,
// og «＋ Ny kontakt» (tilgivende id — bare localpart holder). Ren DOM.
//
// «KOPIER»-KNAPPEN ER FJERNET — DEN VAR BEVISET PÅ EN FEIL:
// Hver kontaktrad hadde en «Kopier»-knapp hvis eneste jobb var å legge Matrix-
// adressen på utklippstavla, fordi «1:1» bare forhåndsutfylte et ANNET panel
// («GNÅ — 1:1 / melding ut») der operatøren måtte lime adressen inn selv. Vi
// hadde altså bygget en knapp for å kompensere for at to av systemets egne
// funksjoner ikke var koblet sammen. Nå åpner «1:1» samtalen direkte i
// Feltveksleren (`Sentralbord.apneDm`), og da har kopiering ingen jobb igjen:
// ingen operativ oppgave krever at en id går via utklippstavla.
import {
  orgDirectory, mergeContacts, searchContacts, normalizeMatrixId, contactShortName,
  gnaUserIdFromPhone, type Contact,
} from '@skade/contracts';

export interface DirectoryHooks {
  /** Åpne 1:1-SAMTALEN mot en Matrix-id (Feltveksler), ikke et skjema. */
  onMessage?: (matrixUserId: string, label: string) => void;
  /** Lagrede GNÅ-kontakter (persisteres av main). */
  getSaved?: () => Contact[];
  /** Auto-lærte kontakter (fra GNÅ-innboksen). */
  getSeen?: () => Contact[];
  /** Lagre en ny kontakt. */
  onAdd?: (name: string, id: string) => void;
  /** Fjern en lagret kontakt. */
  onRemove?: (id: string) => void;
  /** Hjemmeserver-domene til id-normalisering. */
  defaultDomain?: string;
  /**
   * Søk i katalogen over ALLE registrerte GNÅ-brukere (hub: `/gna/brukere`).
   *
   * Eierkrav 2026-08-13: «absolutt alle som har registrert seg i GNÅ SKAL finnes
   * i kontakter i SKADE». Uten denne kroken viste Kontakter bare HV-enhetene,
   * kontakter operatøren selv hadde lagret, og folk som alt hadde skrevet til
   * ham — altså ikke de som faktisk er registrert. Søk på «dan» ga to
   * enhetskontoer og ingen mennesker.
   *
   * Injisert (ikke `fetch` her) fordi panelet enhetstestes i jsdom uten nett.
   */
  sokGnaBrukere?: (q: string) => Promise<{ matrixId: string; navn: string; nummer: string }[]>;
}

const SOURCE_BADGE: Record<Contact['source'], string> = { unit: 'Enhet', saved: 'Lagret', seen: 'Sett', derived: 'Fra nummer' };

/** Minste antall tegn før vi spør hubben. Ett tegn treffer halve registeret. */
const MIN_KATALOGSOK = 2;

export class DirectoryPanel {
  readonly el: HTMLElement;
  private open = false;
  private listEl!: HTMLElement;
  private formEl!: HTMLElement;
  private q = '';
  /** Treff fra GNÅ-katalogen for gjeldende søk (hub-oppslag). */
  private katalog: Contact[] = [];
  private katalogTimer: number | null = null;

  constructor(private hooks: DirectoryHooks = {}, embedded = false) {
    this.el = this.build();
    if (embedded) { this.open = true; this.el.classList.add('open', 'embedded'); }
    this.render();
  }

  toggle() { this.open = !this.open; this.el.classList.toggle('open', this.open); if (this.open) this.render(); }

  /** Alle kontakter (enheter + lagrede + sett), deduplisert. */
  private allContacts(): Contact[] {
    const units: Contact[] = orgDirectory().map((e) => ({
      id: e.matrixUserId,
      name: e.label,
      sub: [e.kind === 'distrikt' ? 'Distriktsstab' : `Kompani · ${e.district}`, e.municipalities?.length ? e.municipalities.join(', ') : ''].filter(Boolean).join(' · '),
      source: 'unit' as const,
    }));
    const saved = this.hooks.getSaved?.() ?? [];
    const seen = this.hooks.getSeen?.() ?? [];
    return mergeContacts(saved, seen, units); // saved > seen > unit ved dublett
  }

  private build(): HTMLElement {
    const p = div('report-composer directory-panel');
    const head = div('report-head');
    head.append(span('report-title', 'Kontakter'));
    const x = document.createElement('button'); x.className = 'report-x'; x.textContent = '×'; x.title = 'Lukk'; x.style.marginLeft = 'auto'; x.onclick = () => this.toggle();
    head.append(x);
    p.append(head);

    const bar = div('calendar-bar');
    const search = document.createElement('input');
    search.className = 'report-input'; search.placeholder = 'Søk: navn, enhet, kommune, id …'; search.style.flex = '1';
    search.oninput = () => { this.q = search.value; this.render(); this.sokKatalog(search.value); };
    bar.append(search);
    if (this.hooks.onAdd) bar.append(btn('＋ Ny', () => { this.formEl.style.display = this.formEl.style.display === 'none' ? '' : 'none'; }));
    p.append(bar);

    this.formEl = this.buildForm(); this.formEl.style.display = 'none';
    p.append(this.formEl);
    this.listEl = div('directory-list');
    p.append(this.listEl);
    return p;
  }

  private buildForm(): HTMLElement {
    const f = div('calendar-form');
    const name = input('Navn (f.eks. Kaptein Dan)');
    const id = input('GNÅ-id (f.eks. gna4791994407 — domenet fylles ut)');
    f.append(field('Navn', name), field('GNÅ Matrix-id', id));
    const actions = div('calendar-form-actions');
    actions.append(btn('Lagre kontakt', () => {
      const nid = normalizeMatrixId(id.value, this.hooks.defaultDomain ?? 'matrix.follodefence.com');
      if (!nid) return;
      this.hooks.onAdd?.(name.value.trim() || contactShortName(nid), nid);
      name.value = ''; id.value = ''; f.style.display = 'none'; this.render();
    }));
    actions.append(btn('Avbryt', () => { f.style.display = 'none'; }));
    f.append(actions);
    return f;
  }

  /**
   * Slår opp registrerte GNÅ-brukere i hubben mens operatøren skriver. Dempet
   * 200 ms, og svar på et gammelt søk forkastes. Feil svelges med vilje:
   * katalogen er et TILLEGG til de lokale kontaktene, så en nede-hub skal ikke
   * gjøre Kontakter ubrukelig.
   */
  private sokKatalog(q: string): void {
    const hook = this.hooks.sokGnaBrukere;
    if (!hook) return;
    if (this.katalogTimer !== null) clearTimeout(this.katalogTimer);
    const trimmet = q.trim();
    if (trimmet.length < MIN_KATALOGSOK) {
      if (this.katalog.length) { this.katalog = []; this.render(); }
      return;
    }
    this.katalogTimer = setTimeout(() => {
      this.katalogTimer = null;
      hook(trimmet)
        .then((treff) => {
          if (this.q.trim() !== trimmet) return;
          this.katalog = treff.map((t) => ({
            id: t.matrixId,
            name: t.navn,
            sub: t.nummer ? `Registrert GNÅ-bruker · ${t.nummer}` : 'Registrert GNÅ-bruker',
            source: 'seen' as const,
          }));
          this.render();
        })
        .catch(() => { /* lokale treff står */ });
    }, 200) as unknown as number;
  }

  render() {
    const hits = searchContacts(this.allContacts(), this.q, 120);
    // Registrerte GNÅ-brukere fra hub-katalogen. Legges til SIST og bare når de
    // ikke alt står i lista, så en lagret kontakt beholder navnet operatøren
    // har gitt den.
    for (const k of this.katalog) {
      if (!hits.some((c) => c.id === k.id)) hits.push(k);
    }
    // Telefonnummer i søkefeltet: GNÅ-id-er er UTLEDET av nummeret («gna» +
    // E.164, samme regel som GNÅ-appen), så et nummer skal aldri gi «Ingen
    // treff» — vi kan alltid tilby den utledede adressen som kontakt. Uten
    // dette var det umulig å finne en GNÅ-bruker som ikke alt hadde skrevet
    // til deg.
    const gna = gnaUserIdFromPhone(this.q, this.hooks.defaultDomain ?? 'matrix.follodefence.com');
    if (gna && !hits.some((c) => c.id === gna)) {
      hits.push({
        id: gna,
        name: `GNÅ-bruker ${this.q.trim()}`,
        sub: 'Utledet fra telefonnummer — har nummeret GNÅ-konto, når du fram her',
        source: 'derived',
      });
    }
    this.listEl.innerHTML = '';
    if (!hits.length) { this.listEl.append(span('roster-empty', this.q ? 'Ingen treff' : 'Ingen kontakter ennå — legg til med «＋ Ny».')); return; }
    for (const c of hits) this.listEl.append(this.row(c));
  }

  private row(c: Contact): HTMLElement {
    const row = div('directory-row');
    const main = div('directory-main');
    const title = div('directory-label-row');
    title.append(span('directory-label', c.name), span(`contact-badge badge-${c.source}`, SOURCE_BADGE[c.source]));
    main.append(title);
    if (c.sub) main.append(span('checklist-note', c.sub));
    main.append(span('directory-mxid', c.id));
    row.append(main);
    if (this.hooks.onMessage) {
      const msg = document.createElement('button'); msg.className = 'targetlist-btn'; msg.textContent = '1:1';
      msg.title = 'Åpne 1:1-samtale i Feltveksler';
      msg.onclick = () => this.hooks.onMessage!(c.id, c.name);
      row.append(msg);
    }
    if (c.source === 'saved' && this.hooks.onRemove) {
      const rm = document.createElement('button'); rm.className = 'targetlist-btn'; rm.textContent = '✕'; rm.title = 'Fjern kontakt';
      rm.onclick = () => { this.hooks.onRemove!(c.id); this.render(); };
      row.append(rm);
    }
    return row;
  }
}

function div(cls: string): HTMLElement { const e = document.createElement('div'); e.className = cls; return e; }
function span(cls: string, text: string): HTMLElement { const e = document.createElement('span'); e.className = cls; e.textContent = text; return e; }
function btn(text: string, run: () => void): HTMLButtonElement { const b = document.createElement('button'); b.className = 'targetlist-btn'; b.textContent = text; b.onclick = run; return b; }
function input(placeholder: string): HTMLInputElement { const i = document.createElement('input'); i.className = 'calendar-input'; i.placeholder = placeholder; return i; }
function field(label: string, control: HTMLElement): HTMLElement { const w = div('calendar-field'); w.append(span('calendar-field-label', label), control); return w; }
